// Holiday schedule: which Jewish holidays take the WEEKDAY Minyanim Rolls off
// the air, and exactly when.
//
// An admin ticks holiday groups in Settings → Holidays; each tick stores one
// boolean on the organization. This module owns everything else — which
// holidays a group covers, and the window a ticked group suppresses the weekday
// schedule for — so the live display, the Dashboard preview and the roll
// editors' live preview can never disagree about whether a roll is on screen.
//
//   GROUP                      COVERS
//   Major Yom Tovs             Pesach · Shavuos · Succos · Shemini Atzeres /
//                              Simchas Torah · Rosh Hashana · Yom Kippur
//   Chol HaMoed                Chol HaMoed Pesach · Chol HaMoed Succos ·
//                              Hoshana Rabba
//   Purim / Purim Katan        Purim · Shushan Purim · Purim Katan ·
//                              Shushan Purim Katan
//   Chanukah                   Chanukah
//
// Only Major Yom Tovs carry the full life-cycle:
//
//   EREV YOM TOV — 12:00 PM   weekday minyanim hide, so the erev Mincha /
//                             candle-lighting schedule owns the screen.
//   ACTIVE YOM TOV            hidden (nightfall → nightfall).
//   MOTZEI — until 9:59 PM    hidden, keeping Maariv / Havdalah on screen.
//   MOTZEI — 10:00 PM         the weekday schedule returns, in time for late
//                             Maariv and tomorrow's Shacharis.
//
// with one exception: a Yom Tov that ends straight into Shabbos (or into
// another Yom Tov / Chol HaMoed) never reaches the 10:00 PM restore — the
// incoming holy day owns the screen instead.
//
// The other three groups suppress the halachic day alone (nightfall →
// nightfall): no noon cutoff on their eve, and the weekday schedule is back the
// moment they end. Fast days — Ta'anis Esther, Tisha B'Av, the minor fasts —
// never suppress anything; they are not one of the groups.
//
// WHICH rolls a holiday hides is a second set of ticks, one per weekday roll
// type — Weekday (Strict) / (Custom) / (Always), see HOLIDAY_ROLL_TYPE_OPTIONS.
// A roll is hidden only when its type is ticked AND a ticked holiday is in
// effect: the two lists intersect. All three start ticked, which is exactly
// what a ticked holiday did before the choice existed. Shabbos, Rosh Chodesh
// and Yom Tov rolls have no tick because they are never touched.
//
// The Jewish day turns over in the evening exactly like the rest of the
// display: at candle lighting (sunset − 18m) when a plain day gives way to a
// day that is assur bemelacha, and at nightfall (tzais) otherwise. That is the
// same rule utils/holiday_header.js uses for the banner, so the two can never
// describe the same moment differently.
//
// Like the banner resolver this file is dependency-injected — the caller hands
// over the already-loaded kosher-zmanim module — so it stays pure logic, adds
// nothing to the entry bundle, and owns a private ComplexZmanimCalendar rather
// than mutating the display's shared one. Every entry point is defensive: on
// any unexpected failure nothing is suppressed, which leaves the schedule
// exactly as it was before this feature existed.

import { RollType } from '@/utils/minyanRollEngine';

const MINUTES_NOON = 12 * 60;
/** Motzei Yom Tov: the weekday schedule comes back at 10:00 PM sharp. */
const MOTZEI_RESTORE_MINUTES = 22 * 60;
const MS_PER_MINUTE = 60000;
const DEFAULT_CANDLE_OFFSET_MINUTES = 18;
// When getTzais() is unavailable, nightfall falls back to sunset + 50m (the
// same magnitude as the display's tzeit50min fallback chain).
const FALLBACK_TZAIS_OFFSET_MINUTES = 50;

export type HolidayGroupKey = 'majorYomTov' | 'cholHamoed' | 'purim' | 'chanukah';

/** How long a ticked group keeps the weekday schedule off the screen. */
export type HolidayLifecycle =
  /** Erev noon → the holiday → 10:00 PM after it ends. */
  | 'yomTov'
  /** The halachic day alone (nightfall → nightfall). */
  | 'day';

export interface HolidayDef {
  /** JewishCalendar constant name, read off the class at runtime. */
  constant: string;
  label: string;
}

export interface HolidayGroupDef {
  key: HolidayGroupKey;
  /** The organization column this group's tick is stored in. */
  settingKey: string;
  label: string;
  /** The holidays, spelled out for the settings row. */
  summary: string;
  /** What ticking it does, in one sentence. */
  description: string;
  lifecycle: HolidayLifecycle;
  /** The phrase used for this group's active phase ("Yom Tov", "Chanukah"…). */
  phaseLabel: string;
  holidays: HolidayDef[];
}

/**
 * The four groups, in the order they are offered in Settings → Holidays. Adding
 * a group means adding one entry here and one boolean column on the
 * organization — the settings panel, the display gate and the roll editor's
 * badge all read this list.
 */
export const HOLIDAY_GROUPS: HolidayGroupDef[] = [
  {
    key: 'majorYomTov',
    settingKey: 'hideWeekdayOnMajorYomTov',
    label: 'Major Yom Tovs',
    summary: 'Pesach, Shavuos, Succos, Rosh Hashana, Yom Kippur',
    description:
      'Weekday minyanim hide from 12:00 PM on erev Yom Tov, stay hidden through the Yom Tov, and return at 10:00 PM after it ends — unless it runs straight into Shabbos.',
    lifecycle: 'yomTov',
    phaseLabel: 'Yom Tov',
    holidays: [
      { constant: 'PESACH', label: 'Pesach' },
      { constant: 'SHAVUOS', label: 'Shavuos' },
      { constant: 'SUCCOS', label: 'Succos' },
      { constant: 'SHEMINI_ATZERES', label: 'Shemini Atzeres' },
      { constant: 'SIMCHAS_TORAH', label: 'Simchas Torah' },
      { constant: 'ROSH_HASHANA', label: 'Rosh Hashana' },
      { constant: 'YOM_KIPPUR', label: 'Yom Kippur' },
    ],
  },
  {
    key: 'cholHamoed',
    settingKey: 'hideWeekdayOnCholHamoed',
    label: 'Chol HaMoed',
    summary: 'Chol HaMoed Pesach & Succos, Hoshana Rabba',
    description:
      'Weekday minyanim hide for each Chol HaMoed day, from nightfall to nightfall.',
    lifecycle: 'day',
    phaseLabel: 'Chol HaMoed',
    holidays: [
      { constant: 'CHOL_HAMOED_PESACH', label: 'Chol HaMoed Pesach' },
      { constant: 'CHOL_HAMOED_SUCCOS', label: 'Chol HaMoed Succos' },
      { constant: 'HOSHANA_RABBA', label: 'Hoshana Rabba' },
    ],
  },
  {
    key: 'purim',
    settingKey: 'hideWeekdayOnPurim',
    label: 'Purim / Purim Katan',
    summary: 'Purim, Shushan Purim, Purim Katan, Shushan Purim Katan',
    description:
      'Weekday minyanim hide for the Purim day itself, from nightfall to nightfall. Ta’anis Esther is unaffected.',
    lifecycle: 'day',
    phaseLabel: 'Purim',
    holidays: [
      { constant: 'PURIM', label: 'Purim' },
      { constant: 'SHUSHAN_PURIM', label: 'Shushan Purim' },
      { constant: 'PURIM_KATAN', label: 'Purim Katan' },
      { constant: 'SHUSHAN_PURIM_KATAN', label: 'Shushan Purim Katan' },
    ],
  },
  {
    key: 'chanukah',
    settingKey: 'hideWeekdayOnChanukah',
    label: 'Chanukah',
    summary: 'All eight days of Chanukah',
    description:
      'Weekday minyanim hide for each day of Chanukah, from nightfall to nightfall.',
    lifecycle: 'day',
    phaseLabel: 'Chanukah',
    holidays: [{ constant: 'CHANUKAH', label: 'Chanukah' }],
  },
];

/** The three weekday roll types a ticked holiday can hide, keyed like the group ticks. */
export type HolidayRollTypeKey = 'weekdayStrict' | 'weekdayCustom' | 'weekdayAlways';

export interface HolidayRollTypeDef {
  key: HolidayRollTypeKey;
  /** The organization column this roll type's tick is stored in. */
  settingKey: string;
  /** The Minyanim Roll type it stands for (minyanRollEngine). */
  rollType: RollType;
  label: string;
  /** Which rolls this covers, in one sentence. */
  description: string;
}

/**
 * The weekday roll types a ticked holiday can take off the air, in the order
 * Settings → Holidays offers them. A holiday hides a roll only when BOTH its
 * group and the roll's type are ticked — the two lists intersect. Every
 * organization starts with all three ticked, which is exactly what a ticked
 * holiday did before this choice existed (every weekday roll hidden).
 */
export const HOLIDAY_ROLL_TYPE_OPTIONS: HolidayRollTypeDef[] = [
  {
    key: 'weekdayStrict',
    settingKey: 'holidayHideWeekdayStrict',
    rollType: RollType.WeekdayStrict,
    label: 'Weekday (Strict)',
    description: 'Rolls that are on air inside the organization\'s Weekday (Strict) window.',
  },
  {
    key: 'weekdayCustom',
    settingKey: 'holidayHideWeekdayCustom',
    rollType: RollType.WeekdayCustom,
    label: 'Weekday (Custom)',
    description: 'Rolls that run on the weekdays and hours chosen in the roll itself.',
  },
  {
    key: 'weekdayAlways',
    settingKey: 'holidayHideWeekdayAlways',
    rollType: RollType.WeekdayAlways,
    label: 'Weekday (Always)',
    description: 'Rolls that are on air all week, whenever nothing outranks them.',
  },
];

/** The saved ticks: one boolean per holiday group and one per weekday roll type. */
export type HolidaySettings = Record<HolidayGroupKey, boolean> & Record<HolidayRollTypeKey, boolean>;

/**
 * A stored tick, or `fallback` when the row does not carry the column at all.
 * The API returns real booleans; a multipart round-trip can leave a string.
 */
function readTick(value: unknown, fallback: boolean): boolean {
  if (value === undefined || value === null || value === '') return fallback;
  return value === true || value === 'true';
}

/** Read every tick off an organization row (or anything shaped like one). */
export function resolveHolidaySettings(organization?: any): HolidaySettings {
  const settings = {} as HolidaySettings;
  // A group column that is missing reads as off: holidays are opt-in.
  HOLIDAY_GROUPS.forEach((group) => {
    settings[group.key] = readTick(organization?.[group.settingKey], false);
  });
  // A roll-type column that is missing reads as ON. That is the behaviour a
  // ticked holiday had before roll types could be chosen (every weekday roll
  // hidden), so a row from an older API — or one the backend has not
  // backfilled yet — keeps hiding exactly what it hid.
  HOLIDAY_ROLL_TYPE_OPTIONS.forEach((option) => {
    settings[option.key] = readTick(organization?.[option.settingKey], true);
  });
  return settings;
}

/** True when at least one holiday group is ticked — a holiday can be in effect. */
export function hasAnyHolidayGroupTicked(settings?: HolidaySettings | null): boolean {
  if (!settings) return false;
  return HOLIDAY_GROUPS.some((group) => Boolean(settings[group.key]));
}

/** The weekday roll types the ticks hide, in option order (possibly none). */
export function resolveHolidayRollTypes(settings?: HolidaySettings | null): RollType[] {
  if (!settings) return [];
  return HOLIDAY_ROLL_TYPE_OPTIONS
    .filter((option) => Boolean(settings[option.key]))
    .map((option) => option.rollType);
}

/** Labels for a set of roll types, in option order. */
export function getHolidayRollTypeLabels(rollTypes: RollType[]): string[] {
  const set = new Set(rollTypes);
  return HOLIDAY_ROLL_TYPE_OPTIONS
    .filter((option) => set.has(option.rollType))
    .map((option) => option.label);
}

/**
 * The rolls a sentence is about: "Weekday" when every type is ticked,
 * otherwise the ticked labels joined — "Weekday (Strict) and Weekday (Custom)".
 * Empty when none is ticked (callers phrase that case themselves).
 */
export function describeHolidayRollTypes(rollTypes: RollType[]): string {
  const labels = getHolidayRollTypeLabels(rollTypes);
  if (!labels.length) return '';
  if (labels.length === HOLIDAY_ROLL_TYPE_OPTIONS.length) return 'Weekday';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * True when the ticks can actually take something off the air: at least one
 * holiday group AND at least one weekday roll type.
 */
export function hasHolidaySuppressionEnabled(settings?: HolidaySettings | null): boolean {
  return hasAnyHolidayGroupTicked(settings) && resolveHolidayRollTypes(settings).length > 0;
}

/** Which stage of a holiday the weekday schedule is being suppressed for. */
export type HolidayPhase = 'erev' | 'active' | 'motzei';

export interface HolidaySuppression {
  /**
   * True when at least one weekday roll type is ticked, i.e. this holiday is
   * actually taking rolls off the air. False when a ticked holiday is in
   * effect but no roll type is ticked — Settings reports that state; the
   * display hides nothing. Which rolls exactly: {@link rollTypes}.
   */
  suppressesWeekday: boolean;
  /** The weekday roll types the ticked settings hide, in option order. */
  rollTypes: RollType[];
  /** Their labels, for messages ("Weekday (Strict)" …). */
  rollTypeLabels: string[];
  groupKey: HolidayGroupKey;
  groupLabel: string;
  /** The holiday itself, e.g. "Pesach" or "Chol HaMoed Succos". */
  holidayLabel: string;
  phase: HolidayPhase;
  /** "Erev Pesach" / "Yom Tov" / "Motzei Pesach" — the state, for a badge. */
  phaseLabel: string;
  /** One sentence saying when the weekday schedule comes back. */
  detail: string;
}

export interface HolidaySuppressionResolverOptions {
  /** The already-imported kosher-zmanim module. */
  kosherZmanim: any;
  latitude: number | string | null | undefined;
  longitude: number | string | null | undefined;
  timezone?: string | null;
  inIsrael?: boolean;
  candleOffsetMinutes?: number;
}

export interface HolidaySuppressionResolver {
  resolve(now: Date, settings?: HolidaySettings | null): HolidaySuppression | null;
}

/* -------------------------------------------------------------------------- */

/**
 * A coordinate that was actually given, or null. `Number(null)` is 0 and
 * `Number('')` is 0, and 0 is a real latitude — so an absent coordinate has to
 * be rejected before the conversion, or a screen with no location would resolve
 * its nightfall on the equator.
 */
function toCoordinate(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function zmanToMs(value: any): number | null {
  if (!value) return null;
  try {
    if (typeof value.toMillis === 'function') {
      const ms = value.toMillis();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof value.toJSDate === 'function') {
      const date = value.toJSDate();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.getTime();
    }
  } catch (error) {
    return null;
  }
  return null;
}

/**
 * Build the holiday-index → group/label lookup from {@link HOLIDAY_GROUPS},
 * resolving each constant off the JewishCalendar class handed in. Reading the
 * values at runtime rather than hard-coding them means a kosher-zmanim upgrade
 * that renumbers (or drops) a constant degrades to "not covered" instead of
 * silently matching the wrong holiday.
 */
function buildHolidayIndex(JewishCalendar: any): Map<number, { group: HolidayGroupDef; label: string }> {
  const byIndex = new Map<number, { group: HolidayGroupDef; label: string }>();
  HOLIDAY_GROUPS.forEach((group) => {
    group.holidays.forEach((holiday) => {
      const index = Number(JewishCalendar?.[holiday.constant]);
      if (!Number.isFinite(index) || index < 0) return;
      if (byIndex.has(index)) return;
      byIndex.set(index, { group, label: holiday.label });
    });
  });
  return byIndex;
}

interface HolidayDayInfo {
  /** JS weekday of the civil date (Sun=0 … Sat=6). */
  dayIndex: number;
  yomTovIndex: number;
  /** Group + label when this day is one of the covered holidays. */
  match: { group: HolidayGroupDef; label: string } | null;
  /** True when work is forbidden — the day the Jewish date flips at candles. */
  isAssurYomTov: boolean;
  isCholHamoed: boolean;
  /** Candle lighting (sunset − offset) and nightfall, in epoch ms. */
  candleMs: number | null;
  tzaisMs: number | null;
}

export function createHolidaySuppressionResolver(
  options: HolidaySuppressionResolverOptions
): HolidaySuppressionResolver | null {
  const {
    kosherZmanim,
    latitude,
    longitude,
    timezone,
    inIsrael = false,
    candleOffsetMinutes = DEFAULT_CANDLE_OFFSET_MINUTES,
  } = options || ({} as HolidaySuppressionResolverOptions);

  const JewishCalendar = kosherZmanim && kosherZmanim.JewishCalendar;
  if (typeof JewishCalendar !== 'function') return null;

  const holidayIndex = buildHolidayIndex(JewishCalendar);

  // ---- private zmanim calendar (never the display's shared one) ------------
  let zmanimCalendar: any = null;
  try {
    const { ComplexZmanimCalendar, GeoLocation } = kosherZmanim;
    const lat = toCoordinate(latitude);
    const lng = toCoordinate(longitude);
    if (
      typeof ComplexZmanimCalendar === 'function' &&
      typeof GeoLocation === 'function' &&
      lat !== null &&
      lng !== null
    ) {
      const zone =
        (typeof timezone === 'string' && timezone.trim()) ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC';
      zmanimCalendar = new ComplexZmanimCalendar(
        new GeoLocation('holiday-schedule', lat, lng, 0, zone)
      );
    }
  } catch (error) {
    // Degrade to civil-day boundaries: the Jewish day then turns at midnight
    // rather than at candles / nightfall, which is the best a location-less
    // resolver can do.
    zmanimCalendar = null;
  }

  // ---- civil date parts in the screen's timezone ---------------------------
  let partsFormatter: Intl.DateTimeFormat | null = null;
  try {
    partsFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: (typeof timezone === 'string' && timezone.trim()) || undefined,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
  } catch (error) {
    partsFormatter = null; // invalid tz string — fall back to device-local below
  }

  function getCivilParts(now: Date) {
    const fallback = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      minutesOfDay: now.getHours() * 60 + now.getMinutes(),
    };
    if (!partsFormatter) return fallback;
    try {
      const map: Record<string, string> = {};
      partsFormatter.formatToParts(now).forEach((part) => {
        map[part.type] = part.value;
      });
      const year = Number(map.year);
      const month = Number(map.month);
      const day = Number(map.day);
      const hours = Number(map.hour);
      const minutes = Number(map.minute);
      if (![year, month, day, hours, minutes].every(Number.isFinite)) return fallback;
      return { year, month, day, minutesOfDay: hours * 60 + minutes };
    } catch (error) {
      return fallback;
    }
  }

  // ---- per-civil-date info, memoized --------------------------------------
  // Values are pure functions of the civil date, so a tiny keyed cache keeps
  // the once-a-second display tick cheap.
  const dayInfoCache = new Map<string, HolidayDayInfo>();

  function buildDayInfo(year: number, month: number, day: number): HolidayDayInfo {
    // Device-local noon of the civil Y/M/D: both kosher-zmanim calendars read a
    // JS Date's local Y/M/D, so this pins them to the intended date with no DST
    // or midnight boundary ambiguity, whatever timezone the device runs in.
    const localNoon = new Date(year, month - 1, day, 12, 0, 0, 0);

    const info: HolidayDayInfo = {
      dayIndex: localNoon.getDay(),
      yomTovIndex: -1,
      match: null,
      isAssurYomTov: false,
      isCholHamoed: false,
      candleMs: null,
      tzaisMs: null,
    };

    try {
      const jc = new JewishCalendar(localNoon);
      if (typeof jc.setInIsrael === 'function') jc.setInIsrael(Boolean(inIsrael));

      const index = Number(jc.getYomTovIndex());
      info.yomTovIndex = Number.isFinite(index) ? index : -1;
      info.match = holidayIndex.get(info.yomTovIndex) || null;
      try {
        info.isAssurYomTov = Boolean(jc.isYomTovAssurBemelacha());
      } catch (error) { /* keep false */ }
      try {
        info.isCholHamoed = Boolean(jc.isCholHamoed());
      } catch (error) { /* keep false */ }
    } catch (error) {
      // Calendar failure — behave like a plain weekday (nothing suppressed).
    }

    if (zmanimCalendar) {
      try {
        zmanimCalendar.setDate(localNoon);
        const sunsetMs =
          zmanToMs(zmanimCalendar.getSunset()) || zmanToMs(zmanimCalendar.getSeaLevelSunset());
        const tzaisMs = zmanToMs(zmanimCalendar.getTzais());
        const offset = Number.isFinite(Number(candleOffsetMinutes))
          ? Number(candleOffsetMinutes)
          : DEFAULT_CANDLE_OFFSET_MINUTES;
        if (sunsetMs != null) info.candleMs = sunsetMs - offset * MS_PER_MINUTE;
        info.tzaisMs =
          tzaisMs != null
            ? tzaisMs
            : sunsetMs != null
              ? sunsetMs + FALLBACK_TZAIS_OFFSET_MINUTES * MS_PER_MINUTE
              : null;
        // Guard against degenerate high-latitude output: nightfall must never
        // precede candle lighting.
        if (info.candleMs != null && info.tzaisMs != null && info.tzaisMs < info.candleMs) {
          info.tzaisMs = info.candleMs;
        }
      } catch (error) {
        info.candleMs = null;
        info.tzaisMs = null;
      }
    }

    return info;
  }

  function getDayInfo(year: number, month: number, day: number): HolidayDayInfo {
    const key = `${year}-${month}-${day}`;
    const cached = dayInfoCache.get(key);
    if (cached) return cached;
    const info = buildDayInfo(year, month, day);
    dayInfoCache.set(key, info);
    if (dayInfoCache.size > 12) {
      // Drop the oldest entry; the cache only ever needs today ± a day.
      dayInfoCache.delete(dayInfoCache.keys().next().value as string);
    }
    return info;
  }

  function getNextCivilDate(year: number, month: number, day: number) {
    const next = new Date(year, month - 1, day + 1, 12, 0, 0, 0);
    return { year: next.getFullYear(), month: next.getMonth() + 1, day: next.getDate() };
  }

  function isEnabled(settings: HolidaySettings | null | undefined, group: HolidayGroupDef): boolean {
    return Boolean(settings && settings[group.key]);
  }

  function resolve(now: Date, settings?: HolidaySettings | null): HolidaySuppression | null {
    // A ticked GROUP is what puts a holiday in effect. Which rolls it then takes
    // off the air is the roll-type ticks, resolved once here and carried on the
    // result: with none ticked the holiday still resolves (so Settings can say
    // "in effect, hiding nothing") but suppressesWeekday is false and the
    // engine's gate (isRollHiddenByHoliday) leaves every roll alone.
    if (!hasAnyHolidayGroupTicked(settings)) return null;
    const rollTypes = resolveHolidayRollTypes(settings);
    const rollTypeLabels = getHolidayRollTypeLabels(rollTypes);
    // "Weekday" / "Weekday (Strict) and Weekday (Custom)" — the rolls a
    // sentence is about, at the start and in the middle of one.
    const subject = describeHolidayRollTypes(rollTypes);
    const subjectMid = subject === 'Weekday' ? 'weekday' : subject;
    const makeSuppression = (
      group: HolidayGroupDef,
      label: string,
      phase: HolidayPhase,
      phaseLabel: string,
      detail: string
    ): HolidaySuppression => ({
      suppressesWeekday: rollTypes.length > 0,
      rollTypes,
      rollTypeLabels,
      groupKey: group.key,
      groupLabel: group.label,
      holidayLabel: label,
      phase,
      phaseLabel,
      detail: rollTypes.length
        ? detail
        : `${label} is in effect, but no weekday roll type is ticked, so nothing is hidden.`,
    });

    const parts = getCivilParts(now);
    const today = getDayInfo(parts.year, parts.month, parts.day);
    const next = getNextCivilDate(parts.year, parts.month, parts.day);
    const tomorrow = getDayInfo(next.year, next.month, next.day);

    const nowMs = now.getTime();
    const todayIsKodesh = today.isAssurYomTov || today.dayIndex === 6;
    const tomorrowIsKodesh = tomorrow.isAssurYomTov || tomorrow.dayIndex === 6;

    // The evening turnover of the Jewish day: candle lighting when a plain day
    // gives way to a holy one, nightfall in every other case. With no zmanim
    // the day only turns at civil midnight.
    const candleFlip = tomorrowIsKodesh && !todayIsKodesh;
    const flipMs = (candleFlip ? today.candleMs : today.tzaisMs) ?? today.tzaisMs;
    const afterFlip = flipMs != null && nowMs >= flipMs;

    // 1 — the Jewish day in progress is itself a covered holiday.
    const current = afterFlip ? tomorrow : today;
    if (current.match && isEnabled(settings, current.match.group)) {
      const { group, label } = current.match;
      return makeSuppression(
        group,
        label,
        'active',
        group.lifecycle === 'yomTov' ? group.phaseLabel : label,
        group.lifecycle === 'yomTov'
          ? `${subject} minyanim are hidden for ${label} and return at 10:00 PM once it ends.`
          : `${subject} minyanim are hidden for ${label} and return at nightfall.`
      );
    }

    // 2 — Erev Yom Tov, from 12:00 PM. Major Yom Tovs only: the other groups
    // have no erev cutoff, so their eve runs the regular weekday schedule.
    const incoming = tomorrow.match;
    if (
      !afterFlip &&
      parts.minutesOfDay >= MINUTES_NOON &&
      incoming &&
      incoming.group.lifecycle === 'yomTov' &&
      isEnabled(settings, incoming.group)
    ) {
      return makeSuppression(
        incoming.group,
        incoming.label,
        'erev',
        `Erev ${incoming.label}`,
        `${subject} minyanim hide from 12:00 PM on erev ${incoming.label} so the erev schedule owns the screen.`
      );
    }

    // 3 — Motzei Yom Tov. The night after a Major Yom Tov keeps the Maariv /
    // Havdalah schedule until 10:00 PM, then hands the screen back to the
    // weekday roll. A Yom Tov that runs straight into Shabbos, another Yom Tov
    // or Chol HaMoed never reaches that restore: the incoming holy day owns the
    // screen instead.
    const ended = today.match;
    if (
      afterFlip &&
      ended &&
      ended.group.lifecycle === 'yomTov' &&
      isEnabled(settings, ended.group)
    ) {
      const intoShabbos = tomorrow.dayIndex === 6;
      const intoHolyDay = intoShabbos || tomorrow.isAssurYomTov || tomorrow.isCholHamoed;
      if (intoHolyDay) {
        return makeSuppression(
          ended.group,
          ended.label,
          'motzei',
          `Motzei ${ended.label}`,
          intoShabbos
            ? `${ended.label} runs straight into Shabbos, so ${subjectMid} minyanim stay hidden instead of returning at 10:00 PM.`
            : `${ended.label} runs straight into the next holy day, so ${subjectMid} minyanim stay hidden.`
        );
      }
      if (parts.minutesOfDay < MOTZEI_RESTORE_MINUTES) {
        return makeSuppression(
          ended.group,
          ended.label,
          'motzei',
          `Motzei ${ended.label}`,
          `${subject} minyanim return at 10:00 PM, in time for late Maariv.`
        );
      }
    }

    return null;
  }

  return {
    resolve(now, settings) {
      try {
        const date = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
        return resolve(date, settings);
      } catch (error) {
        // Never let a calendar failure take a schedule off the screen.
        return null;
      }
    },
  };
}
